/**
 * A premise's opening hours as whole hours of a 24-hour clock — the range the widget's curves
 * cover. Stored per premise in RDS's `app_settings.gp_widget.hours` (the same place the widget
 * theme and chain id live), and overridable per embed with `data-hours`.
 */
export interface HourRange {
  /** First hour shown, 0-23. */
  start: number;
  /** First hour *not* shown, 1-24 — so `{ start: 6, end: 24 }` is 06:00 to midnight. */
  end: number;
}

/** Shortest range worth drawing a curve across. */
const MIN_SPAN_HOURS = 2;

/**
 * Reads an hour range from a stored setting or an embed attribute. Accepts the way opening
 * hours are written, `"6-24"` (06:00 to midnight), or an object with the same two numbers.
 * Returns null for anything invalid and for the full day, which needs no restricting — one
 * code path for "no range".
 */
export function parseHourRange(value: unknown): HourRange | null {
  const pair = hourPair(value);
  if (!pair) return null;
  const [start, end] = pair;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
  if (start < 0 || end > 24 || end - start < MIN_SPAN_HOURS) return null;
  return start === 0 && end === 24 ? null : { start, end };
}

function hourPair(value: unknown): [number, number] | null {
  if (typeof value === "string") {
    const match = /^(\d{1,2})\s*[-–]\s*(\d{1,2})$/.exec(value.trim());
    return match ? [Number(match[1]), Number(match[2])] : null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.start === "number" && typeof record.end === "number") return [record.start, record.end];
  }
  return null;
}
